import { Link } from "react-router-dom";
import { Maximize2, MoreHorizontal, Settings, VideoOff, WifiOff } from "lucide-react";
import { useDeviceFrame } from "@/hooks/useDeviceFrame";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

function stateFor({ enabled, runtime, signal, src }) {
  if (!enabled) return { label: "Désactivée", dot: "bg-zinc-500", tone: "text-zinc-400", icon: VideoOff };
  if (runtime !== "online") return { label: "Hors ligne", dot: "bg-red-500", tone: "text-zinc-400", icon: WifiOff };
  if (signal === "waiting" || !src) return { label: "Connexion…", dot: "bg-amber-400", tone: "text-zinc-300", icon: null };
  if (signal === "lost") return { label: "Hors ligne", dot: "bg-red-500", tone: "text-zinc-400", icon: WifiOff };
  return { label: "En ligne", dot: "bg-emerald-500", tone: "text-zinc-200", icon: null };
}

export function DeviceStreamCard({ camera, pollMs = 200, onFullscreen }) {
  const runtime = camera.runtime_status || "offline";
  const active = camera.enabled && runtime === "online";
  const { src, signal, ratio } = useDeviceFrame({ cameraId: camera.id, active, pollMs });
  const state = stateFor({ enabled: camera.enabled, runtime, signal, src });
  const StateIcon = state.icon;
  const live = state.label === "En ligne";

  return (
    <article
      className="group relative overflow-hidden rounded-2xl border border-white/[0.08] bg-[#111114] shadow-sm shadow-black/20 transition duration-200 hover:-translate-y-0.5 hover:border-white/[0.14]"
      data-testid={`camera-stream-card-${camera.id}`}
    >
      <button
        type="button"
        onClick={() => onFullscreen?.(camera)}
        style={{ aspectRatio: ratio ? String(ratio) : "16 / 9" }}
        className="relative block w-full overflow-hidden bg-black text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset"
        data-testid={`camera-card-video-${camera.id}`}
        aria-label={`Ouvrir ${camera.name} en plein écran`}
      >
        {live && src ? (
          <img src={src} alt={camera.name} className="absolute inset-0 h-full w-full bg-black object-contain" data-testid={`camera-stream-frame-${camera.id}`} />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.035),_transparent_58%)] text-zinc-500">
            {StateIcon ? <StateIcon className="size-8 text-zinc-600" strokeWidth={1.35} /> : null}
            <span className={`text-sm font-medium ${state.tone} ${state.label === "Connexion…" ? "animate-pulse" : ""}`}>{state.label}</span>
          </div>
        )}

        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 bg-gradient-to-t from-black/80 via-black/35 to-transparent px-4 pb-3 pt-12">
          <div className="min-w-0">
            <div className="truncate text-[15px] font-semibold text-white" data-testid={`camera-stream-name-${camera.id}`}>{camera.name}</div>
            <div className="mt-1 flex items-center gap-2 text-xs">
              <span className={`size-1.5 rounded-full ${state.dot} ${live ? "shadow-[0_0_0_3px_rgba(16,185,129,0.10)]" : ""}`} />
              <span className={state.tone} data-testid={`camera-stream-status-${camera.id}`}>{state.label}</span>
            </div>
          </div>
          <Maximize2 className="hidden size-4 text-white/60 transition-opacity sm:block sm:opacity-0 sm:group-hover:opacity-100" strokeWidth={1.6} />
        </div>
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="absolute right-3 top-3 z-10 grid size-11 place-items-center rounded-xl bg-black/55 text-white/80 backdrop-blur-sm transition hover:bg-black/70 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
            data-testid={`camera-card-menu-${camera.id}`}
            aria-label={`Menu de ${camera.name}`}
            title={`Menu de ${camera.name}`}
          >
            <MoreHorizontal className="size-[18px]" strokeWidth={1.8} />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="border-white/[0.08] bg-[#18181b] text-zinc-200">
          <DropdownMenuItem asChild className="min-h-11 cursor-pointer focus:bg-white/[0.07] focus:text-white">
            <Link to={`/cameras/${camera.id}`}>
              <Settings className="size-4" strokeWidth={1.7} />
              Paramètres
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </article>
  );
}
